import { DomainType } from "../../models/domain";
import { deleteEndpoint, get, post, put } from "../../utils/net";

import type { DomainTypeEnum } from "../../models/domain";
import type {
  EncoderUser,
  EncoderUserEntity,
  EncoderUserListResultEntity,
  FacilityRolesEntity,
  UserFeature,
  UserRolesSet,
} from "../../models/user";

const baseURL = `${process.env.REACT_APP_API_PATH}/api/User`;

const domainUrl = {
  [DomainType.Facility]: "FacilityRoles",
  [DomainType.Practice]: "PracticeRoles",
};

const mapEncoderUserListResultToEntity = (list): EncoderUserListResultEntity => ({
  users: list.map((user: any) => mapEncoderUserResultToEntity(user)),
  count: list.count,
  total_count: list.total_count,
});

const mapEncoderUserResultToEntity = (user: EncoderUser): EncoderUserEntity => ({
  id: user.id || user.UserId,
  clientId: user.ClientId,
  customerId: user.CustomerId,
  userId: user.UserId,
  firstName: user.FirstName,
  lastName: user.LastName,
  userName: user.UserName,
  email: user.Email,
  isItAdmin: user.IsItAdmin || false,
  isSystemAdmin: user.IsSystemAdmin,
  status: user.Status,
  version: user.Version,
  invitationLink: user.InvitationLink,
  roles: [],
  licensePackageId: user.LicensePackageId || "",
  mailSent: user.MailSent,
  originalOrganizationId: user.OriginalOrganizationId,
  errorDetails: user.ErrorDetails,
  defaultRole: user.DefaultRole,
});

const createBasicExtraOptions = () => ({
  useExactPath: true,
});

const createIfMatchHeaders = (version: string) => ({
  "If-Match": version,
});

const createExtraOptionsWithHeaders = (headers: Record<string, string>) => ({
  headers,
  useExactPath: true,
});

const getList = (): Promise<EncoderUserListResultEntity> => {
  const url = `${baseURL}/List`;

  return get(url, createBasicExtraOptions()).then((result: any) =>
    mapEncoderUserListResultToEntity(result.body),
  );
};

const getUser = (id: string): Promise<EncoderUserEntity> => {
  const url = `${baseURL}/${id}`;

  return get(url, createBasicExtraOptions()).then((result: any) =>
    mapEncoderUserResultToEntity(result.body),
  );
};

const getUserRoles = async (id: string, domain: DomainTypeEnum): Promise<FacilityRolesEntity[]> => {
  const url = `${baseURL}/${id}/${domainUrl[domain]}`;

  const result = await get(url, createBasicExtraOptions());

  return result.body;
};

const inviteUser = (user: EncoderUserEntity, sendEmail?: boolean): Promise<EncoderUserEntity> => {
  const url = `${baseURL}/Invite?sendEmail=${sendEmail}`;

  return post(url, user, createBasicExtraOptions()).then((result: any) =>
    result.body.map(mapEncoderUserResultToEntity),
  );
};

const saveUserChanges = (user: EncoderUserEntity): Promise<EncoderUserEntity> => {
  const url = `${baseURL}/${user.userId}`;
  const headers = createIfMatchHeaders(user.version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  return post(url, user, extraOptions).then((result: any) =>
    mapEncoderUserResultToEntity(result.body),
  );
};

const saveUserRoles = async (
  userWithRoles: EncoderUserEntity,
  domain: DomainTypeEnum,
): Promise<{ count: number }> => {
  const url = `${baseURL}/${userWithRoles.userId}/${domainUrl[domain]}`;
  const headers = createIfMatchHeaders(userWithRoles.version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  const mappedRoles = userWithRoles.roles.map((r) => mapToServiceFacilityRole(r));

  const result = await post(url, mappedRoles, extraOptions);

  return result.body;
};

const saveSingleFacilityRole = async (
  userId: string,
  facilityId: string,
  role: string,
  version: string,
  domain: DomainTypeEnum = DomainType.Facility,
): Promise<void> => {
  const url = `${baseURL}/${userId}/${domainUrl[domain]}`;
  const headers = createIfMatchHeaders(version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  const roleData = [
    {
      FacilityId: facilityId,
      Role: role,
      UserId: userId,
    },
  ];

  await post(url, roleData, extraOptions);
};

const setAllFacilityRoles = async (
  userId: string,
  role: string,
  version: string,
  domain: DomainTypeEnum = DomainType.Facility,
): Promise<void> => {
  const url = `${baseURL}/${userId}/${domainUrl[domain]}/SetAll?role=${role}`;
  const headers = createIfMatchHeaders(version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  await put(url, null, extraOptions);
};

const saveSingleFacilityProFee = async (
  userId: string,
  facilityId: string,
  allowProfessionalCoding: boolean,
  version: string,
  currentRole?: string,
): Promise<void> => {
  const url = `${baseURL}/${userId}/FacilityRoles`;
  const headers = createIfMatchHeaders(version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  const roleData = [
    {
      FacilityId: facilityId,
      AllowProfessionalCoding: allowProfessionalCoding,
      UserId: userId,
      Role: currentRole,
    },
  ];

  await post(url, roleData, extraOptions);
};

const setAllFacilitiesProFee = async (
  userId: string,
  allowProfessionalCoding: boolean,
  version: string,
): Promise<void> => {
  const url = `${baseURL}/${userId}/FacilityRoles/SetAll/ProFee?isAllowed=${allowProfessionalCoding}`;
  const headers = createIfMatchHeaders(version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  await put(url, null, extraOptions);
};

const mapToServiceFacilityRole = (role: FacilityRolesEntity): FacilityRolesEntity => ({
  Facility: role.Facility,
  FacilityId: role.FacilityId,
  Role: role.Role,
  UserId: role.UserId,
  Version: role.Version,
  FacilityIsActive: role.FacilityIsActive,
  AllowProfessionalCoding: role.AllowProfessionalCoding,
});

const deleteInvitedUser = (user: EncoderUserEntity): Promise<void> => {
  const url = `${baseURL}/${user.userId}`;
  const headers = createIfMatchHeaders(user.version);
  const extraOptions = createExtraOptionsWithHeaders(headers);

  return deleteEndpoint(url, extraOptions).then((result: any) => result);
};

const auditLogout = () => {
  const url = `${baseURL}/AuditLogout`;

  return post(url, {}, createBasicExtraOptions()).then(() => true);
};

const resetPassword = (id: string) => {
  const url = `${baseURL}/ResetPassword`;

  return post(url, { id }, createBasicExtraOptions()).then(() => true);
};

const resetAllPasswords = () => {
  const url = `${baseURL}/ResetPassword/All`;

  return post(url, {}, createBasicExtraOptions()).then(() => true);
};

export const getUserFeatures = async (): Promise<UserFeature[]> => {
  const response = await get(
    `${process.env.REACT_APP_API_PATH}/api/User/Features`,
    createBasicExtraOptions(),
  );

  return response.body;
};

export const getUserRolesSet = async (domain: DomainTypeEnum): Promise<{ Roles: UserRolesSet }> => {
  const response = await get(
    `${process.env.REACT_APP_API_PATH}/api/User/${domainUrl[domain]}`,
    createBasicExtraOptions(),
  );

  return response.body;
};

export const userAPI = {
  deleteInvitedUser,
  getList,
  getUser,
  getUserRoles,
  saveUserChanges,
  saveUserRoles,
  saveSingleFacilityRole,
  setAllFacilityRoles,
  saveSingleFacilityProFee,
  setAllFacilitiesProFee,
  inviteUser,
  auditLogout,
  resetPassword,
  resetAllPasswords,
};
